import React from 'react';
import styled from 'styled-components';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { useCreateEpdCO2UptakeAssociation, useUpdateEpdCO2UptakeAssociation } from 'services/api/mutations';
import { useGetEpdCO2UptakeAssociation } from 'services/api/queries';
import { FieldPanelFullWidth, FieldPanelHeader } from 'styles/v2/Styles.styled';

import { YES_NO } from '../../constants';
import DeclarationOfCO2Grid from '../../grids/declaration-of-co2/DeclarationOfCo2Grid';

const DeclarationOfCO2Panel: React.FunctionComponent<{
  epdVersionId: string;
  errors: any;
}> = ({ epdVersionId, errors }) => {
  const cO2UptakeAssociation = useGetEpdCO2UptakeAssociation(epdVersionId).data;
  const createCO2UptakeAssociation = useCreateEpdCO2UptakeAssociation(epdVersionId);
  const updateCO2UptakeAssociation = useUpdateEpdCO2UptakeAssociation(epdVersionId);

  const handleCO2UptakeAssociationUpdate = (field: string, value: string) => {
    if (!cO2UptakeAssociation?.id) {
      createCO2UptakeAssociation.mutate({ epdVersionId, association: value });
      return;
    }
    updateCO2UptakeAssociation.mutate({ id: cO2UptakeAssociation.id, epdVersionId, association: value });
  };

  const co2Options = [
    {
      value: YES_NO.YES,
      label: (
        <>
          Yes, CO<SubWithNoLineGap>2</SubWithNoLineGap> uptaking associated with carbonation is included in the modules
        </>
      ),
    },
    {
      value: YES_NO.NO,
      label: (
        <>
          No, there is no CO<SubWithNoLineGap>2</SubWithNoLineGap> uptaking associated with carbonation in any of the modules
        </>
      ),
    },
  ];

  const errorForEntireBlock = errors?.cO2Carbonation || errors?.declarationOfCO2Assumptions;

  return (
    <FieldPanelFullWidth>
      <FieldPanelHeader>
        <span>
          CO<SubWithNoLineGap>2</SubWithNoLineGap> Carbonation
        </span>
        <TooltipHelpIcon content={'Assumptions on CO₂ uptake associated with carbonation shall be specified if included.'} />
        {errorForEntireBlock && <TooltipErrorIcon content={errorForEntireBlock} />}
      </FieldPanelHeader>
      <CompilerSelectControlledComponent
        label={
          <>
            CO<sub>2</sub> uptake associated with carbonation
          </>
        }
        name="association"
        options={co2Options}
        value={co2Options.find(o => o.value === cO2UptakeAssociation?.association)}
        onChanged={handleCO2UptakeAssociationUpdate}
        required={false}
        placeholder="Select"
        isClearable={false}
      />
      <DeclarationOfCO2Grid
        epdVersionId={epdVersionId}
        disabled={cO2UptakeAssociation?.association !== YES_NO.YES}
        error={errors?.declarationOfCO2Assumptions}
      />
    </FieldPanelFullWidth>
  );
};

export default DeclarationOfCO2Panel;

const SubWithNoLineGap = styled.sub`
  vertical-align: baseline;
  position: relative;
  top: 0.33em;
`;
